import { api } from './api';

export interface TierLimits {
  clients: number;
  invoicesMonthly: number;
  contractsMonthly: number;
  projects: number;
  tasks: number;
  notes: number;
  landingPages: number;
  leadsMonthly: number;
  aiActionsMonthly: number;
  templates: number;
  storageGb: number;
  community: boolean;
  analytics: boolean;
  aiChat: boolean;
  export: boolean;
  whiteLabel: boolean;
}

export interface Usage {
  clients: number;
  projects: number;
  tasks: number;
  notes: number;
  templates: number;
  landingPages: number;
  invoicesMonthly: number;
  contractsMonthly: number;
  leadsMonthly: number;
  aiActionsMonthly: number;
  storageUsedBytes: number;
}

export interface SubscriptionData {
  tier: 'trial' | 'launch' | 'rise' | 'summit' | 'expired';
  status: 'trialing' | 'active' | 'past_due' | 'canceled' | 'unpaid' | 'expired';
  billingCycle: 'monthly' | 'annual' | null;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  trialEnd: string | null;
  trialDaysRemaining: number;
  cancelAtPeriodEnd: boolean;
  isUsable: boolean;
}

export interface SubscriptionResponse {
  subscription: SubscriptionData;
  usage: Usage;
  limits: TierLimits;
}

export interface Invoice {
  id: string;
  number: string | null;
  status: string | null;
  amount: number;
  currency: string;
  created: string;
  periodStart: string | null;
  periodEnd: string | null;
  pdfUrl: string | null;
  hostedUrl: string | null;
}

export interface PaymentMethod {
  type: string;
  card: {
    brand: string;
    last4: string;
    expMonth: number;
    expYear: number;
  } | null;
}

export interface PlanChangePreview {
  prorationAmount: number;
  newAmount: number;
  immediateCharge: number;
}

/**
 * Get subscription details, usage, and limits
 */
export async function getSubscription(): Promise<SubscriptionResponse> {
  const response = await api.get('/subscription');
  return response.data;
}

/**
 * Create a Stripe Checkout session
 */
export async function createCheckout(
  tier: 'launch' | 'rise' | 'summit',
  billingCycle: 'monthly' | 'annual',
  successUrl?: string,
  cancelUrl?: string
): Promise<{ url: string }> {
  const response = await api.post('/subscription/checkout', {
    tier,
    billingCycle,
    successUrl,
    cancelUrl,
  });
  return response.data;
}

/**
 * Create a Stripe Customer Portal session
 */
export async function createPortalSession(
  returnUrl?: string
): Promise<{ url: string }> {
  const response = await api.post('/subscription/portal', { returnUrl });
  return response.data;
}

/**
 * Get current usage
 */
export async function getUsage(): Promise<Usage> {
  const response = await api.get('/subscription/usage');
  return response.data;
}

/**
 * Get invoice history
 */
export async function getInvoices(limit?: number): Promise<{ invoices: Invoice[] }> {
  const response = await api.get('/subscription/invoices', {
    params: { limit },
  });
  return response.data;
}

/**
 * Get current payment method
 */
export async function getPaymentMethod(): Promise<{ paymentMethod: PaymentMethod | null }> {
  const response = await api.get('/subscription/payment-method');
  return response.data;
}

/**
 * Preview proration for plan change
 */
export async function previewPlanChange(
  tier: 'launch' | 'rise' | 'summit',
  billingCycle: 'monthly' | 'annual'
): Promise<PlanChangePreview> {
  const response = await api.post('/subscription/preview-change', {
    tier,
    billingCycle,
  });
  return response.data;
}

/**
 * Cancel subscription immediately
 */
export async function cancelSubscription(): Promise<{ success: boolean; message: string }> {
  const response = await api.post('/subscription/cancel');
  return response.data;
}

export default {
  getSubscription,
  createCheckout,
  createPortalSession,
  getUsage,
  getInvoices,
  getPaymentMethod,
  previewPlanChange,
  cancelSubscription,
};
